import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Download, Send, FileText } from 'lucide-react'
import { formatDate } from '../../lib/calculations'
import { authedFetch } from '../../lib/authedFetch'
import { getNextInvoiceNumber, saveInvoice, getInvoices } from '../../lib/publicData'

const DEPT_BUSINESS_MAP = {
  kitchen: 'Desres Kitchen',
  bakery: 'Desres Kitchen',
  tuckshop: 'Desres Kitchen',
  yawehs: "Yahweh's Delight",
}

const EMPTY_INVOICE_ITEM = { description: '', quantity: 1, unitPrice: 0 }

export default function InvoicesTab({ addToast, department, prefillOrder, clearPrefill }) {
  const deptBusiness = DEPT_BUSINESS_MAP[department]
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [business, setBusiness] = useState(deptBusiness || 'Desres Kitchen')
  const [items, setItems] = useState([{ ...EMPTY_INVOICE_ITEM }])
  const [notes, setNotes] = useState('')
  const [includeVat, setIncludeVat] = useState(true)
  const [sending, setSending] = useState(false)
  const [generating, setGenerating] = useState(false)

  const fetchInvoices = async () => {
    const data = await getInvoices()
    setInvoices(data)
    setLoading(false)
  }

  useEffect(() => { fetchInvoices() }, [])

  useEffect(() => {
    if (prefillOrder) {
      setCustomerName(prefillOrder.name || '')
      setCustomerEmail(prefillOrder.email || '')
      setCustomerPhone(prefillOrder.whatsapp || '')
      setBusiness(prefillOrder.business || 'Desres Kitchen')
      setItems((prefillOrder.items || []).map(i => ({
        description: i.description || i.name || '',
        quantity: i.quantity || 1,
        unitPrice: i.price || 0,
      })))
      setCreating(true)
      clearPrefill?.()
    }
  }, [prefillOrder])

  const subtotal = items.reduce((sum, i) => sum + (i.quantity * i.unitPrice), 0)
  const vat = includeVat ? subtotal * 0.15 : 0
  const total = subtotal + vat

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: field === 'description' ? value : Number(value) || 0 } : item))
  }
  const addItem = () => setItems(prev => [...prev, { ...EMPTY_INVOICE_ITEM }])
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx))

  const generatePdf = async (invoiceNumber) => {
    const { default: jsPDF } = await import('jspdf')
    await import('jspdf-autotable')
    const doc = new jsPDF()
    const pw = doc.internal.pageSize.width

    doc.setFillColor(245, 241, 236)
    doc.rect(0, 0, pw, 40, 'F')
    doc.setDrawColor(192, 57, 43)
    doc.setLineWidth(0.8)
    doc.line(0, 40, pw, 40)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(20)
    doc.setTextColor(28, 20, 16)
    doc.text(business, 14, 22)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(156, 136, 120)
    doc.text('INVOICE', 14, 32)

    doc.setFontSize(10)
    doc.setTextColor(156, 136, 120)
    doc.text('Invoice No.', pw - 80, 18)
    doc.text('Date', pw - 80, 26)
    doc.setTextColor(28, 20, 16)
    doc.setFont('helvetica', 'bold')
    doc.text(invoiceNumber, pw - 14, 18, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    doc.text(new Date().toLocaleDateString('en-ZA'), pw - 14, 26, { align: 'right' })

    let y = 52
    doc.setTextColor(156, 136, 120)
    doc.setFontSize(9)
    doc.text('BILL TO', 14, y)
    y += 6
    doc.setTextColor(28, 20, 16)
    doc.setFontSize(11)
    doc.text(customerName || 'Customer', 14, y)
    if (customerEmail) { y += 5; doc.setFontSize(9); doc.text(customerEmail, 14, y) }
    if (customerPhone) { y += 5; doc.setFontSize(9); doc.text(customerPhone, 14, y) }

    y += 12
    doc.autoTable({
      startY: y,
      head: [['Item', 'Qty', 'Unit Price', 'Total']],
      body: items.filter(i => i.description).map(i => [
        i.description,
        String(i.quantity),
        `R${Number(i.unitPrice).toFixed(2)}`,
        `R${(i.quantity * i.unitPrice).toFixed(2)}`,
      ]),
      styles: { fontSize: 10, cellPadding: 4, textColor: [28, 20, 16] },
      headStyles: { fillColor: [245, 241, 236], textColor: [192, 57, 43], fontStyle: 'bold', fontSize: 8, letterSpacing: 0.5 },
      alternateRowStyles: { fillColor: [253, 251, 247] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 20 },
        2: { halign: 'right', cellWidth: 30 },
        3: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
      },
      margin: { left: 14, right: 14 },
    })

    y = doc.lastAutoTable.finalY + 10
    const rightX = pw - 14
    doc.setFontSize(10)
    doc.setTextColor(156, 136, 120)
    doc.text('Subtotal', rightX - 50, y)
    doc.setTextColor(28, 20, 16)
    doc.text(`R${subtotal.toFixed(2)}`, rightX, y, { align: 'right' })

    if (includeVat) {
      y += 7
      doc.setTextColor(156, 136, 120)
      doc.text('VAT (15%)', rightX - 50, y)
      doc.setTextColor(28, 20, 16)
      doc.text(`R${vat.toFixed(2)}`, rightX, y, { align: 'right' })
    }

    y += 3
    doc.setDrawColor(192, 57, 43)
    doc.setLineWidth(0.5)
    doc.line(rightX - 60, y, rightX, y)
    y += 8
    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(192, 57, 43)
    doc.text('Total Due', rightX - 50, y)
    doc.text(`R${total.toFixed(2)}`, rightX, y, { align: 'right' })

    if (notes) {
      y += 16
      doc.setFillColor(245, 241, 236)
      doc.roundedRect(14, y - 4, pw - 28, 20, 2, 2, 'F')
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(92, 74, 58)
      doc.text(`Notes: ${notes}`, 18, y + 4, { maxWidth: pw - 36 })
    }

    doc.setFontSize(9)
    doc.setTextColor(156, 136, 120)
    doc.text('Thank you for your business!', pw / 2, doc.internal.pageSize.height - 14, { align: 'center' })

    return doc
  }

  const handleDownload = async () => {
    if (!items.some(i => i.description && i.unitPrice > 0)) {
      addToast('Add at least one item with a price', 'error')
      return
    }
    setGenerating(true)
    try {
      const invNum = await getNextInvoiceNumber()
      const invoiceNumber = `INV-${String(invNum).padStart(4, '0')}`
      const doc = await generatePdf(invoiceNumber)
      doc.save(`${invoiceNumber}.pdf`)
      await saveInvoice({ invoiceNumber, customerName, customerEmail, customerPhone, business, items: items.filter(i => i.description), subtotal, vat, total, notes, includeVat })
      addToast(`Invoice ${invoiceNumber} downloaded`, 'success')
      fetchInvoices()
    } catch (err) {
      addToast('Failed to generate invoice: ' + err.message, 'error')
    }
    setGenerating(false)
  }

  const handleSendEmail = async () => {
    if (!customerEmail) { addToast('Customer email is required to send', 'error'); return }
    if (!items.some(i => i.description && i.unitPrice > 0)) { addToast('Add at least one item with a price', 'error'); return }
    setSending(true)
    try {
      const invNum = await getNextInvoiceNumber()
      const invoiceNumber = `INV-${String(invNum).padStart(4, '0')}`
      const res = await authedFetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: customerEmail,
          invoiceNumber,
          business,
          customerName,
          items: items.filter(i => i.description),
          subtotal,
          vat,
          total,
          notes,
          date: new Date().toLocaleDateString('en-ZA'),
        }),
      })
      if (!res.ok) throw new Error((await res.json()).error || 'Send failed')
      await saveInvoice({ invoiceNumber, customerName, customerEmail, customerPhone, business, items: items.filter(i => i.description), subtotal, vat, total, notes, includeVat, sentViaEmail: true })
      addToast(`Invoice ${invoiceNumber} sent to ${customerEmail}`, 'success')
      fetchInvoices()
      resetForm()
    } catch (err) {
      addToast('Failed to send invoice: ' + err.message, 'error')
    }
    setSending(false)
  }

  const resetForm = () => {
    setCustomerName('')
    setCustomerEmail('')
    setCustomerPhone('')
    setBusiness(deptBusiness || 'Desres Kitchen')
    setItems([{ ...EMPTY_INVOICE_ITEM }])
    setNotes('')
    setIncludeVat(true)
    setCreating(false)
  }

  if (loading) return <p className="text-gray-500 text-sm text-center py-8">Loading invoices...</p>

  if (creating) {
    return (
      <div className="space-y-5 max-w-3xl">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">New Invoice</h3>
          <button onClick={resetForm} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Customer Name</label>
            <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer name" className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Email</label>
            <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="customer@email.com" className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Phone</label>
            <input type="text" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="0812345678" className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Business</label>
            <select value={business} onChange={e => setBusiness(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full">
              <option>Desres Kitchen</option>
              <option>Yahweh's Delight</option>
            </select>
          </div>
          <div className="flex items-end gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={includeVat} onChange={e => setIncludeVat(e.target.checked)} className="rounded bg-gray-800 border-gray-700" />
              Include VAT (15%)
            </label>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-2 block">Line Items</label>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 text-[10px] text-gray-500 uppercase tracking-wider px-1">
              <span>Description</span><span className="text-center">Qty</span><span className="text-right">Unit Price</span><span className="text-right">Total</span><span></span>
            </div>
            {items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_70px_90px_90px_32px] gap-2 items-center">
                <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Item description" className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white" />
                <input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white text-center" />
                <input type="number" min="0" step="0.01" value={item.unitPrice || ''} onChange={e => updateItem(idx, 'unitPrice', e.target.value)} placeholder="0.00" className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white text-right" />
                <div className="text-sm text-gray-300 text-right font-medium pr-1">R{(item.quantity * item.unitPrice).toFixed(2)}</div>
                {items.length > 1 && (
                  <button onClick={() => removeItem(idx)} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addItem} className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1 mt-2">
            <Plus size={12} /> Add line item
          </button>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Payment terms, special instructions..." className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full" />
        </div>

        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 max-w-xs ml-auto">
          <div className="flex justify-between text-sm text-gray-400 mb-1">
            <span>Subtotal</span><span className="text-white">R{subtotal.toFixed(2)}</span>
          </div>
          {includeVat && (
            <div className="flex justify-between text-sm text-gray-400 mb-1">
              <span>VAT (15%)</span><span className="text-white">R{vat.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-bold text-green-400 border-t border-gray-700 pt-2 mt-2">
            <span>Total</span><span>R{total.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={handleDownload} disabled={generating} className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
            <Download size={14} /> {generating ? 'Generating...' : 'Download PDF'}
          </button>
          <button onClick={handleSendEmail} disabled={sending || !customerEmail} className="flex items-center gap-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
            <Send size={14} /> {sending ? 'Sending...' : 'Send via Email'}
          </button>
          <button onClick={resetForm} className="bg-gray-700 text-gray-300 px-4 py-2.5 rounded-lg text-sm">Cancel</button>
        </div>
      </div>
    )
  }

  const deptInvoices = deptBusiness ? invoices.filter(inv => inv.business === deptBusiness) : invoices

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{deptInvoices.length} invoice{deptInvoices.length !== 1 ? 's' : ''} generated · {deptBusiness}</p>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={14} /> New Invoice
        </button>
      </div>

      {deptInvoices.length === 0 ? (
        <div className="text-center py-12">
          <FileText size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No invoices yet</p>
          <p className="text-gray-600 text-xs mt-1">Create one from scratch or from an existing order</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-800">
                <th className="text-left py-2 px-2">Invoice #</th>
                <th className="text-left py-2 px-2">Customer</th>
                <th className="text-right py-2 px-2">Total</th>
                <th className="text-center py-2 px-2">Sent</th>
                <th className="text-left py-2 px-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {deptInvoices.map(inv => (
                <tr key={inv.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="py-2 px-2 font-mono text-xs text-brand-400">{inv.invoiceNumber}</td>
                  <td className="py-2 px-2">
                    <div className="text-white text-xs">{inv.customerName || '—'}</div>
                    {inv.customerEmail && <div className="text-[10px] text-gray-500">{inv.customerEmail}</div>}
                  </td>
                  <td className="py-2 px-2 text-right font-bold text-green-400">R{Number(inv.total).toFixed(2)}</td>
                  <td className="py-2 px-2 text-center">
                    {inv.sentViaEmail
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-900 text-green-300">Emailed</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">Downloaded</span>
                    }
                  </td>
                  <td className="py-2 px-2 text-xs text-gray-500">{formatDate(inv.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
